import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { InboxNotification } from 'src/app/models/core/inbox-notification.model';
import { InboxService } from './inbox.service';
import { CustomToastService } from '../custom-toast.service';

export interface NotificationState {
  notifications: InboxNotification[];
  loading: boolean;
  loadingMore: boolean;
  currentPage: number;
  hasMorePages: boolean;
  unreadCount: number;
}

export interface TabsNotificationState {
  unread: NotificationState;
  read: NotificationState;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationStateService {
  private stateSubject = new BehaviorSubject<TabsNotificationState>({
    unread: {
      notifications: [],
      loading: false,
      loadingMore: false,
      currentPage: 1,
      hasMorePages: false,
      unreadCount: 0
    },
    read: {
      notifications: [],
      loading: false,
      loadingMore: false,
      currentPage: 1,
      hasMorePages: false,
      unreadCount: 0
    }
  });

  public state$ = this.stateSubject.asObservable();

  constructor(
    private inboxService: InboxService,
    private toastService: CustomToastService
  ) { }

  private get currentState(): TabsNotificationState {
    return this.stateSubject.value;
  }

  private updateTabState(tab: 'unread' | 'read', updates: Partial<NotificationState>): void {
    this.stateSubject.next({
      ...this.currentState,
      [tab]: { ...this.currentState[tab], ...updates }
    });
  }

  loadNotifications(tab: 'unread' | 'read', page = 1, append = false, forceRefresh = false): Observable<any> {
    if (page === 1) {
      this.updateTabState(tab, { loading: true });
    } else {
      this.updateTabState(tab, { loadingMore: true });
    }

    const params: any = {
      page,
      per_page: 10,
    };

    if (tab === 'unread') {
      params.unread_only = true;
    } else {
      params.read_only = true;
    }

    if (forceRefresh) {
      params._t = Date.now();
    }

    return this.inboxService.getNotifications(params);
  }

  handleNotificationsResponse(tab: 'unread' | 'read', response: any, append: boolean): void {
    console.log(`Handling ${tab} notifications response:`, response);
    if (response.success) {
      const newNotifications = response.data;
      const currentNotifications = this.currentState[tab].notifications;

      console.log('New notifications:', newNotifications);

      const updatedNotifications = append
        ? [...currentNotifications, ...newNotifications]
        : newNotifications;

      console.log('Updated notifications:', updatedNotifications);

      this.updateTabState(tab, {
        notifications: updatedNotifications,
        unreadCount: tab === 'unread' ? updatedNotifications.length : 0,
        hasMorePages: response.meta.current_page < response.meta.last_page,
        currentPage: response.meta.current_page
      });
    }
  }

  handleLoadError(tab: 'unread' | 'read'): void {
    console.error(`Error loading ${tab} notifications`);
    this.toastService.show('Error al cargar las notificaciones', 3500, 'error');
  }

  handleLoadComplete(tab: 'unread' | 'read'): void {
    this.updateTabState(tab, {
      loading: false,
      loadingMore: false
    });
  }

  markAsRead(notificationId: number, context?: HttpContext): Observable<any> {
    return this.inboxService.markAsRead(notificationId, context);
  }

  handleMarkAsReadResponse(response: any, notificationId: number): void {
    if (response && response.success) {
      const unreadNotifications = [...this.currentState.unread.notifications];
      const notificationIndex = unreadNotifications.findIndex(n => n.id === notificationId);

      if (notificationIndex !== -1) {
        const [notification] = unreadNotifications.splice(notificationIndex, 1);
        notification.status = 'read';
        notification.read_at = new Date().toISOString();

        this.updateTabState('unread', {
          notifications: unreadNotifications,
          unreadCount: unreadNotifications.length
        });

        const readNotifications = [notification, ...this.currentState.read.notifications];
        this.updateTabState('read', {
          notifications: readNotifications
        });
      }
    }
  }

  handleMarkAsReadError(): void {
    console.error('Error marking notification as read');
    this.toastService.show('Error al marcar como leída', 3500, 'error');
  }

  markAllAsRead(): Observable<any> {
    return this.inboxService.markAllAsRead();
  }

  handleMarkAllAsReadResponse(response: any): void {
    if (response.success) {
      this.toastService.show('Todas las notificaciones marcadas como leídas', 3500, 'success');
    }
  }

  handleMarkAllAsReadError(): void {
    console.error('Error marking all as read');
    this.toastService.show('Error al marcar todas como leídas', 3500, 'error');
  }

  canLoadMore(tab: 'unread' | 'read'): boolean {
    const { loadingMore, hasMorePages } = this.currentState[tab];
    return !loadingMore && hasMorePages;
  }

  getNextPage(tab: 'unread' | 'read'): number {
    return this.currentState[tab].currentPage + 1;
  }

  refresh(tab: 'unread' | 'read', forceRefresh = true): Observable<any> {
    return this.loadNotifications(tab, 1, false, forceRefresh);
  }

  clearState(): void {
    this.stateSubject.next({
      unread: {
        notifications: [],
        loading: false,
        loadingMore: false,
        currentPage: 1,
        hasMorePages: false,
        unreadCount: 0
      },
      read: {
        notifications: [],
        loading: false,
        loadingMore: false,
        currentPage: 1,
        hasMorePages: false,
        unreadCount: 0
      }
    });
  }

  getTotalUnreadCount(): number {
    return this.currentState.unread.unreadCount;
  }
}
